import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import NestableClickable from 'components/Common/NestableClickable'
import { ImageParams } from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useCallback, useContext, useRef, useState } from 'react'
import styled from 'styled-components'
import TextButton from '../Button/TextButton'
import LineImagesIcon from '../Icons/line/LineImagesIcon'
import SolidPlayIcon from '../Icons/solid/SolidPlayIcon'
import LightBoxContext from '../Modal/LightBoxContext'
import ImageCarousel from './ImageCarousel'
import ImageGalleryLightbox from './ImageGalleryLightbox'
import VideoLightbox from './VideoLightbox'
import { ImageCarouselRefInterface } from './BaseImageCarousel'

const Container = styled(NestableClickable)`
  position: relative;

  aspect-ratio: var(--gallery-xs-aspect);

  ${mediaQueryUp.sm} {
    aspect-ratio: var(--gallery-sm-aspect, var(--gallery-xs-aspect));
  }

  ${mediaQueryUp.md} {
    aspect-ratio: var(--gallery-md-aspect, var(--gallery-sm-aspect, var(--gallery-xs-aspect)));
  }

  ${mediaQueryUp.lg} {
    aspect-ratio: var(--gallery-lg-aspect, var(--gallery-md-aspect, var(--gallery-sm-aspect, var(--gallery-xs-aspect))));
  }
`

const GalleryButtons = styled(CSSBreakpoint)`
  display: flex;
  pointer-events: auto;
  gap: ${rem(8)};
`

interface Props extends React.ComponentProps<typeof ImageCarousel> {
  videoId?: string;
  onOpen?: () => void;
  onClose?: () => void;
  size?: 'small' | 'large';
}

const defaultImageProps: ImageParams = { xs: '3:2', sm: '16:9', quality: 'good', fit: 'none' }

function ImageGallery(props: Props) {
  const {
    images,
    className,
    videoId,
    size = 'large',
    imageParams = defaultImageProps,
    defaultIndex = 0,
    showImageTitles = true,
    ...carouselProps
  } = props

  const [currentImage, setCurrentImage] = useState<App.Image | undefined>(images[defaultIndex])
  const carouselRef = useRef<ImageCarouselRefInterface>(null)
  const showLightbox = useContext(LightBoxContext)

  const showImageLightbox = async() => {
    let carouselImage = currentImage
    await showLightbox(<ImageGalleryLightbox
      images={images}
      defaultIndex={currentImage ? images.indexOf(currentImage) : 0}
      onImageChange={(image) => {
        carouselImage = image
      }}
    />)
    // done, now sync the image the user was on\
    if (carouselImage) {
      carouselRef.current?.goToImage(carouselImage)
    }
  }
  const showVideoLightbox = () => {
    if (videoId) {
      showLightbox(<VideoLightbox videoId={videoId} playOnMount />)
    }
  }

  const updateCurrentImage = useCallback((index: number, image?: App.Image) => setCurrentImage(image), [])

  return (<Container
      onClick={() => showImageLightbox()}
      className={className}
      style={{
        '--gallery-xs-aspect': imageParams.xs?.replace(':', ' / '),
        '--gallery-sm-aspect': imageParams.sm?.replace(':', ' / '),
        '--gallery-md-aspect': imageParams.md?.replace(':', ' / '),
        '--gallery-xl-aspect': imageParams.xl?.replace(':', ' / '),
      } as React.CSSProperties}
    >
    <ImageCarousel
      {...carouselProps}
      showImageTitles={showImageTitles}
      carouselRef={carouselRef}
      images={images}
      imageParams={imageParams}
      onImageChange={updateCurrentImage}
      bottomRight={<>
        {size === 'large' && <GalleryButtons min="sm">
          {videoId && <TextButton
            kind="secondary"
            variant="dark"
            startIcon={<SolidPlayIcon />}
            onClick={showVideoLightbox}
          >
            Play video
          </TextButton>}
          {images.length > 1 && <TextButton
            kind="secondary"
            variant="dark"
            startIcon={<LineImagesIcon />}
            nonInteractable
          >
            View gallery
          </TextButton>}
        </GalleryButtons>}
        <GalleryButtons max={size === 'large' ? 'xs' : undefined}>
          {videoId && <TextButton
            kind="secondary"
            variant="dark"
            startIcon={<SolidPlayIcon />}
            onClick={showVideoLightbox}
          >
            Play video
          </TextButton>}
        </GalleryButtons>
      </>}
    />
  </Container>)
}

export default ImageGallery
